import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/v2", label: "Home" },
  { to: "/v2/about", label: "About WHO" },
  { to: "/v2/resources", label: "Discover Resources" },
  { to: "/v2/approach", label: "Our Approach" },
  { to: "/v2/partners", label: "Partners" },
  { to: "/v2/contact", label: "Contact" },
];

/** Slim chrome for WHO Website — Version 2 (Simplified). Version 1 is untouched. */
export default function V2Layout({ children }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="v2-root">
      <a href="#v2-main" className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:bg-navy focus:text-cream focus:px-4 focus:py-2">
        Skip to content
      </a>
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-borderSubtle" data-testid="v2-header">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/v2" className="flex items-center gap-2.5" data-testid="v2-logo-link" onClick={() => setOpen(false)}>
            <img src="/assets/who-logo.png" alt="WHO — Warm Hand Off" className="h-10 w-auto object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-6" data-testid="v2-desktop-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/v2"}
                data-testid={`v2-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-tight transition-colors whitespace-nowrap ${
                    isActive ? "text-navy border-b-2 border-gold pb-0.5" : "text-slateSoft hover:text-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <button className="md:hidden p-2 text-navy" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="v2-mobile-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-borderSubtle bg-white" data-testid="v2-mobile-nav">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`v2-mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`block px-6 py-3 text-sm font-semibold border-b border-borderSubtle ${loc.pathname === l.to ? "text-navy bg-cream" : "text-slateSoft"}`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </header>

      <main id="v2-main" className="flex-1" data-testid="v2-main">
        {children}
      </main>

      <footer className="bg-navy-deep text-cream/80" data-testid="v2-footer">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-10">
          <p className="font-semibold text-gold-light tracking-wide text-sm" data-testid="v2-footer-signature">
            Born in Pittsburgh. Built for Veterans. Designed for National Impact.
          </p>
          <p className="mt-2 text-sm text-cream/70">If Not You, Then WHO.</p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-gold-light transition-colors" data-testid={`v2-footer-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-2 text-[11px] text-white/50">
            <p>© 2026 WHO — Warm Hand Off LLC. All rights reserved.</p>
            <p>
              Simplified preview (Version 2) ·{" "}
              <Link to="/" className="underline hover:text-gold-light" data-testid="v2-footer-v1-link">View the comprehensive website</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
